import { createRoute, lazyRouteComponent } from '@tanstack/react-router'
import { z } from 'zod'

import { authenticatedRoute } from '@/app/routes/authenticated'
import { pageSearch as page, textSearch as text } from '@/shared/api/search'
import { requirePermission } from '@/shared/auth/guards'

const projectsSearchSchema = z.object({
  page,
  search: text,
  status: z.enum(['active', 'paused', 'completed', 'archived']).optional().catch(undefined),
  company: text,
})

const tasksSearchSchema = z.object({
  page,
  search: text,
  status: z
    .enum(['requested', 'backlog', 'in_progress', 'review', 'done', 'rejected'])
    .optional()
    .catch(undefined),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional().catch(undefined),
  /** ULID da sprint ou `none` (backlog). */
  sprint: text,
  assignee: text,
})

const sprintsSearchSchema = z.object({
  status: z.enum(['requested', 'planned', 'active', 'completed', 'rejected']).optional().catch(undefined),
})

const allTasksSearchSchema = z.object({
  page,
  search: text,
  status: z
    .enum(['requested', 'backlog', 'in_progress', 'review', 'done', 'rejected'])
    .optional()
    .catch(undefined),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional().catch(undefined),
  project: text,
  assignee: text,
})

const allSprintsSearchSchema = z.object({
  page,
  search: text,
  status: z.enum(['requested', 'planned', 'active', 'completed', 'rejected']).optional().catch(undefined),
  project: text,
})

const projectTicketsSearchSchema = z.object({
  page,
  search: text,
  status: z.enum(['pending', 'resolved', 'closed']).optional().catch(undefined),
})

export const projectsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/projects',
  beforeLoad: requirePermission('projects.view'),
})

export const projectsIndexRoute = createRoute({
  getParentRoute: () => projectsRoute,
  path: '/',
  validateSearch: (search) => projectsSearchSchema.parse(search),
  component: lazyRouteComponent(() => import('./pages/projects-page'), 'ProjectsPage'),
})

export const projectNewRoute = createRoute({
  getParentRoute: () => projectsRoute,
  path: 'new',
  beforeLoad: requirePermission('projects.manage'),
  validateSearch: (search) => z.object({ company: text }).parse(search),
  component: lazyRouteComponent(() => import('./pages/project-new-page'), 'ProjectNewPage'),
})

// Listas de todos os projetos. Rotas estáticas vencem `$projectId`, então `/projects/tasks` não é um projeto.
export const allSprintsRoute = createRoute({
  getParentRoute: () => projectsRoute,
  path: 'sprints',
  validateSearch: (search) => allSprintsSearchSchema.parse(search),
  component: lazyRouteComponent(() => import('./pages/all-sprints-page'), 'AllSprintsPage'),
})

export const allTasksRoute = createRoute({
  getParentRoute: () => projectsRoute,
  path: 'tasks',
  validateSearch: (search) => allTasksSearchSchema.parse(search),
  component: lazyRouteComponent(() => import('./pages/all-tasks-page'), 'AllTasksPage'),
})

// Navegação aninhada: Projetos › Projeto › Tarefas | Sprints | Chamados.
export const projectRoute = createRoute({
  getParentRoute: () => projectsRoute,
  path: '$projectId',
})

export const projectOverviewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/project-page'), 'ProjectPage'),
})

export const projectEditRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: 'edit',
  beforeLoad: requirePermission('projects.manage'),
  component: lazyRouteComponent(() => import('./pages/project-edit-page'), 'ProjectEditPage'),
})

export const projectTasksRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: 'tasks',
  validateSearch: (search) => tasksSearchSchema.parse(search),
  component: lazyRouteComponent(() => import('./pages/project-tasks-page'), 'ProjectTasksPage'),
})

export const projectSprintsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: 'sprints',
  validateSearch: (search) => sprintsSearchSchema.parse(search),
  component: lazyRouteComponent(() => import('./pages/project-sprints-page'), 'ProjectSprintsPage'),
})

export const projectTicketsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: 'tickets',
  beforeLoad: requirePermission('tickets.view'),
  validateSearch: (search) => projectTicketsSearchSchema.parse(search),
  component: lazyRouteComponent(() => import('./pages/project-tickets-page'), 'ProjectTicketsPage'),
})

export const projectsRoutes = projectsRoute.addChildren([
  projectsIndexRoute,
  projectNewRoute,
  allSprintsRoute,
  allTasksRoute,
  projectRoute.addChildren([
    projectOverviewRoute,
    projectEditRoute,
    projectTasksRoute,
    projectSprintsRoute,
    projectTicketsRoute,
  ]),
])
